import {
    type GridApi,
    type GridReadyEvent,
    type SelectionChangedEvent,
} from '@ag-grid-community/core';
import { type ReactNode, useCallback, useMemo } from 'react';
import { ICSAgGrid } from '@components/ICSGrid/ICSAgGrid';
import { type CellRendererParams } from '@components/Table/types';
import { prepareContextMenuItems } from '@components/Table/utils/ContextMenu';
import { RequestTableColumns } from '@pages/notification-components/components/RequestsCard/RequestsTable/models/RequestTableColumns';
import { useRequestTableActionsConfig } from '@pages/notification-components/components/RequestsCard/RequestsTable/utils/useRequestTableActionsConfig';
import { type ComponentCreateRequestMapped } from '@pages/notification-components/models/ComponentCreateRequestMapped';

interface RequestsTableProps {
    requests: ComponentCreateRequestMapped[];
    selectedChange: (request: ComponentCreateRequestMapped) => void;
    gridReady: (api: GridApi<ComponentCreateRequestMapped>) => void;
}

export function RequestsTable({
    requests,
    selectedChange,
    gridReady,
}: RequestsTableProps): ReactNode {
    const actionsConfig = useRequestTableActionsConfig();

    const contextMenuItems = useMemo(
        () => (params: CellRendererParams<ComponentCreateRequestMapped>) =>
            prepareContextMenuItems(actionsConfig, params),
        [actionsConfig],
    );

    const handleSelection = useCallback(
        (params: SelectionChangedEvent<ComponentCreateRequestMapped>): void => {
            const selectedRows = params.api.getSelectedRows();
            if (selectedRows.length > 0) {
                selectedChange(selectedRows[0]);
            }
        },
        [selectedChange],
    );

    const handleGridReady = useCallback(
        (event: GridReadyEvent<ComponentCreateRequestMapped>): void => {
            gridReady(event.api);
        },
        [gridReady],
    );

    return (
        <ICSAgGrid<ComponentCreateRequestMapped>
            id="notification-component-requests"
            tableDescription="List of all requests"
            columnDefs={RequestTableColumns}
            rowData={requests}
            rowSelection="multiple"
            contextMenuItems={contextMenuItems}
            contextMenuSettings={actionsConfig.settings}
            getRowId={(params) => params.data.id ?? ''}
            onSelectionChanged={handleSelection}
            onGridReady={handleGridReady}
            bindSelectedRowsToContext
            domLayout="autoHeight"
        />
    );
}
